import React from "react";
import { FaTrash } from "react-icons/fa";
import { TableSkeleton } from "../common/skeletons";
import ProfilePhotoCell from "../common/ProfilePhotoCell";
import DownloadCsvDropdown from "./DownloadCsvDropdown";

const displayName = (row) =>
  (row.name && String(row.name).trim()) ||
  [row.firstName, row.lastName].filter(Boolean).join(" ").trim() ||
  "—";

const cell = (value) => {
  if (value == null || value === "") return "—";
  return String(value);
};

const CredentialsTable = ({
  credentials = [],
  loading,
  page,
  limit,
  total,
  selectedIds,
  onToggleRow,
  onSelectAllPage,
  onDownloadSelected,
  onDownloadAll,
  onPageChange,
  csvDownloading,
  onDeleteUser,
  onBulkDelete,
  deletingUserId,
  bulkDeleting,
}) => {
  const totalPages = Math.ceil(total / limit) || 1;
  const isAllOnPageSelected =
    credentials.length > 0 && credentials.every((r) => selectedIds.has(r.id));
  const selectedCount = selectedIds.size;

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">All dummy user credentials</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Profile details, photo, and password for each dummy account. Select rows to download CSV or delete.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onBulkDelete && (
            <button
              type="button"
              onClick={onBulkDelete}
              disabled={selectedCount === 0 || bulkDeleting}
              className="rounded-lg border border-red-500 bg-white px-3 py-2 text-xs md:text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkDeleting ? "Deleting…" : `Delete selected (${selectedCount})`}
            </button>
          )}
          <DownloadCsvDropdown
            selectedCount={selectedCount}
            loading={csvDownloading}
            onDownloadSelected={onDownloadSelected}
            onDownloadAll={onDownloadAll}
          />
        </div>
      </div>
      {loading ? (
        <TableSkeleton rows={8} cols={12} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-xs md:text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 w-10 sticky left-0 bg-gray-50 z-10">
                    <input
                      type="checkbox"
                      checked={isAllOnPageSelected}
                      onChange={(e) => onSelectAllPage(e.target.checked)}
                      className="rounded border-gray-300"
                      aria-label="Select all on page"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Photo</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">User ID</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Email</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Password</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Gender</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Age</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Phone</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Country</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Occupation</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Education</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Religion</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Sect</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Marital</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Diet</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Children</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Height</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Career</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Income</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Smoking</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Alcohol</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Languages</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Marriage plan</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Parenting</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Residential</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Tagline</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Created</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600 sticky right-0 bg-gray-50 z-10">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {credentials.length === 0 ? (
                  <tr>
                    <td colSpan={30} className="px-3 py-6 text-center text-gray-500">
                      No dummy user credentials yet. Create users with Auto or Manual above.
                    </td>
                  </tr>
                ) : (
                  credentials.map((row, idx) => {
                    const name = displayName(row);
                    const isDeleting = deletingUserId === row.userId;
                    return (
                      <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                        <td className="px-3 py-2 sticky left-0 bg-white">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.id)}
                            onChange={() => onToggleRow(row.id)}
                            className="rounded border-gray-300"
                            aria-label={`Select row ${row.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className="px-3 py-2">
                          <ProfilePhotoCell imageUrl={row.imageUrl} displayName={name} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.userId}</td>
                        <td className="px-3 py-2 text-gray-900 whitespace-nowrap font-medium">
                          {name}
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-900 whitespace-nowrap">
                          {row.email}
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-700 whitespace-nowrap">
                          {row.password}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.gender)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.age)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.contactNo)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.grewUp || row.countryName)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.occupation)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.education)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.religion)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.sect)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.maritalStatus)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.diet)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.children)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {row.height != null ? `${row.height} cm` : "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.careerStatus)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.monthlyIncome)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.smoking)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.alcohol)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.languages)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.marriagePlan)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.parentingGoal)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {cell(row.residentialPreference)}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-600 max-w-[160px] truncate"
                          title={row.tagline || ""}
                        >
                          {cell(row.tagline)}
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-2 sticky right-0 bg-white">
                          {onDeleteUser && (
                            <button
                              type="button"
                              onClick={() => onDeleteUser(row)}
                              disabled={isDeleting || bulkDeleting}
                              className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-red-600 hover:bg-red-50 disabled:opacity-50"
                              title="Delete user"
                            >
                              <FaTrash className="text-xs" />
                              {isDeleting ? "…" : "Delete"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {total > limit && onPageChange && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages} (Total: {total})
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CredentialsTable;
